<template>
  <div @mouseover="hovered = true" @mouseleave="hovered = false">
    <div class="flex gap-2">
      <div>
        <t-label
          v-if="label"
          :hovered="hovered"
          :changed="changedColor"
          :tooltip="resetTooltip"
          @reset="emit('resetColor')"
          >{{ label }}</t-label
        >
        <color-picker
          :color-picker-top="colorPickerTop"
          no-border-right
          medium
          :default-color="localColor"
          :disabled="disabled"
          :light="light"
          :colors="colors"
          :template-colors="templateColors"
          :template-colors-label="templateColorsLabel"
          :predefined-colors-label="predefinedColorsLabel"
          @color-change="colorChanged"
          @get-template-colors="$emit('get-template-colors')"
          @start-selecting-color="$emit('start-selecting-color')"
          @stop-selecting-color="$emit('stop-selecting-color')"
        ></color-picker>
      </div>
      <div>
        <t-label
          v-if="label"
          :hovered="hovered"
          :changed="changedSize"
          @reset="emit('resetSize')"
          >{{ labelWidth }}</t-label
        >
        <t-dropdown
          ref="dropdownSizeRef"
          class="dropdown-size"
          style="width: 60px"
          with-input
          input-type="number"
          :selected="localSize"
          :label="label ? undefined : labelWidth"
          :imin="0"
          :disabled="disabled"
          @input-changed="widthChanged"
        >
          <template #selected-content>
            <p style="padding-left: 0px">
              {{ localSize ? `${localSize}` : "Size" }}
            </p>
          </template>

          <template #content>
            <ul>
              <VueCustomScrollbar
                style="position: relative; margin: auto; height: 192px"
                :settings="{ wheelPropagation: false }"
              >
                <li
                  v-for="(width, k) in 7"
                  :key="k"
                  style="padding: 4px 0; display: flex; justify-content: flex-start"
                  @click="widthChanged(width - 1)"
                >
                  <p v-if="typeof size === 'number'" style="width: 120px; padding-left: 12px">
                    {{ width - 1 }}
                  </p>
                  <p v-else style="width: 120px; padding-left: 12px">
                    {{ width - 1 }}
                  </p>
                </li>
              </VueCustomScrollbar>
            </ul>
          </template>
        </t-dropdown>
      </div>
      <div data-testid="border-style">
        <t-label
          v-if="label"
          :hovered="hovered"
          :changed="changedStyle"
          @reset="emit('resetStyle')"
          >{{ labelStyle }}</t-label
        >
        <TButtonGroup>
          <TGroupButton
            data="solid"
            small
            :active="borderStyle === 'solid'"
            :disabled="disabled"
            @button-clicked="borderChnaged"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M1 3V17V19H3H17H19V17V3V1H17H3H1V3ZM17 3H3V17H17V3Z"
                fill="currentColor"
              />
            </svg>
          </TGroupButton>
          <TGroupButton
            data="dashed"
            small
            :active="borderStyle === 'dashed'"
            :disabled="disabled"
            @button-clicked="borderChnaged"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M17 1H14V3H17V6H19V3V1H17ZM19 8H17V12H19V8ZM3 14H1V17V19H3H6V17H3V14ZM12 17H8V19H12V17ZM19 14H17V17H14V19H17H19V17V14ZM1 8H3V12H1V8ZM12 1H8V3H12V1ZM3 1H6V3H3V6H1V3V1H3Z"
                fill="currentColor"
              />
            </svg>
          </TGroupButton>
          <TGroupButton
            data="dotted"
            small
            :active="borderStyle === 'dotted'"
            :disabled="disabled"
            @button-clicked="borderChnaged"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M18 1C17.4477 1 17 1.44772 17 2C17 2.55228 17.4477 3 18 3C18.5523 3 19 2.55228 19 2C19 1.44772 18.5523 1 18 1ZM18 5C17.4477 5 17 5.44772 17 6C17 6.55228 17.4477 7 18 7C18.5523 7 19 6.55228 19 6C19 5.44772 18.5523 5 18 5ZM18 9C17.4477 9 17 9.44771 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 9.44771 18.5523 9 18 9ZM18 13C17.4477 13 17 13.4477 17 14C17 14.5523 17.4477 15 18 15C18.5523 15 19 14.5523 19 14C19 13.4477 18.5523 13 18 13ZM1 18C1 17.4477 1.44772 17 2 17C2.55228 17 3 17.4477 3 18C3 18.5523 2.55228 19 2 19C1.44772 19 1 18.5523 1 18ZM6 17C5.44772 17 5 17.4477 5 18C5 18.5523 5.44772 19 6 19C6.55228 19 7 18.5523 7 18C7 17.4477 6.55228 17 6 17ZM9 18C9 17.4477 9.44771 17 10 17C10.5523 17 11 17.4477 11 18C11 18.5523 10.5523 19 10 19C9.44771 19 9 18.5523 9 18ZM14 17C13.4477 17 13 17.4477 13 18C13 18.5523 13.4477 19 14 19C14.5523 19 15 18.5523 15 18C15 17.4477 14.5523 17 14 17ZM18 17C17.4477 17 17 17.4477 17 18C17 18.5523 17.4477 19 18 19C18.5523 19 19 18.5523 19 18C19 17.4477 18.5523 17 18 17ZM1 14C1 13.4477 1.44772 13 2 13C2.55228 13 3 13.4477 3 14C3 14.5523 2.55228 15 2 15C1.44772 15 1 14.5523 1 14ZM1 10C1 9.44771 1.44772 9 2 9C2.55228 9 3 9.44771 3 10C3 10.5523 2.55228 11 2 11C1.44772 11 1 10.5523 1 10ZM1 6C1 5.44772 1.44772 5 2 5C2.55228 5 3 5.44772 3 6C3 6.55228 2.55228 7 2 7C1.44772 7 1 6.55228 1 6ZM13 2C13 1.44772 13.4477 1 14 1C14.5523 1 15 1.44772 15 2C15 2.55228 14.5523 3 14 3C13.4477 3 13 2.55228 13 2ZM10 1C9.44771 1 9 1.44772 9 2C9 2.55228 9.44771 3 10 3C10.5523 3 11 2.55228 11 2C11 1.44772 10.5523 1 10 1ZM5 2C5 1.44772 5.44772 1 6 1C6.55228 1 7 1.44772 7 2C7 2.55228 6.55228 3 6 3C5.44772 3 5 2.55228 5 2ZM2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3C2.55228 3 3 2.55228 3 2C3 1.44772 2.55228 1 2 1Z"
                fill="currentColor"
              />
            </svg>
          </TGroupButton>
        </TButtonGroup>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import TLabel from "../TLabel/TLabel.vue";
import colorPicker from "../TColorPicker/colorPicker.vue";
import TButtonGroup from "../TButtonGroup/TButtonGroup.vue";
import TGroupButton from "../TButtonGroup/_internal/TButton.vue";
import TDropdown from "../TDropdown/TDropdown.vue";
import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
import "vue3-perfect-scrollbar/style.css";
import { ref, computed } from "vue";

type Props = {
  label: string;
  labelWidth: string;
  labelStyle: string;
  templateColorsLabel: string;
  predefinedColorsLabel: string;
  color: string;
  size: number;
  borderStyle: string;
  colorPickerTop?: boolean;
  disabled?: boolean;
  light: boolean;
  colors: string[];
  templateColors: string[];
  changed?: boolean;
  changedColor?: boolean;
  changedSize?: boolean;
  changedStyle?: boolean;
  resetTooltip?: string;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  colorPickerTop: false,
  disabled: false,
  light: false,
  changed: false,
});

const emit = defineEmits([
  "size-changed",
  "color-changed",
  "border-style-changed",
  "get-template-colors",
  "start-selecting-color",
  "stop-selecting-color",
  "reset",
  "resetColor",
  "resetSize",
  "resetStyle",
]);

const hovered = ref(false);

const localSize = computed({
  get() {
    return props.size;
  },
  set(val) {
    emit("size-changed", val);
  },
});

const localColor = computed({
  get() {
    if (props.color === "undefined" || props.color === undefined) {
      return "";
    }

    return props.color;
  },
  set(val) {
    emit("color-changed", val);
  },
});

function widthChanged(val: number | string) {
  emit("size-changed", val);
}

function borderChnaged(val: string) {
  emit("border-style-changed", val);
}

function colorChanged(val: string) {
  emit("color-changed", val);
}
</script>
<style lang="scss"></style>
